import { useMemo } from 'react';
import {
  calculateLotteryTicket,
  calculatorPassConstraint,
  calculatorSelectionLabel,
  MARKET_LABELS,
  type CalculatorMarket,
  type CalculatorMatch,
  type CalculatorMatchSelection,
} from '@nowscore/core';
import type { MatchDto } from '../api';
import { latestLotteryQuotes, lotterySettledQuotes } from '../lib/odds';

export function LotteryCalculatorBar({ matches, selections, passType, multiplier, onPassTypeChange, onMultiplierChange, onRemove, onClear }: {
  matches: MatchDto[];
  selections: Record<string, CalculatorMatchSelection>;
  passType: number;
  multiplier: number;
  onPassTypeChange: (value: number) => void;
  onMultiplierChange: (value: number) => void;
  onRemove: (scheduleId: string) => void;
  onClear: () => void;
}) {
  const selected = matches.map((match) => selections[match.scheduleId]).filter(Boolean) as CalculatorMatchSelection[];
  const calculatorMatches = useMemo<CalculatorMatch[]>(() => matches.map((match) => ({
    scheduleId: match.scheduleId,
    status: match.status,
    homeGoals: match.score.home,
    awayGoals: match.score.away,
    halfHomeGoals: match.score.halfHome,
    halfAwayGoals: match.score.halfAway,
    quotes: (['had', 'hhad', 'ttg', 'crs', 'hafu'] as CalculatorMarket[]).flatMap((market) => latestLotteryQuotes(match, market).map((quote) => ({
      market,
      selection: quote.selection,
      odds: quote.odds,
      lineValue: quote.lineValue,
      lineText: quote.lineText,
      observedAt: quote.observedAt,
    }))),
    settledQuotes: lotterySettledQuotes(match).map((quote) => ({
      market: quote.market,
      selection: quote.selection,
      odds: quote.odds,
      lineValue: quote.lineValue,
      lineText: quote.lineText,
      observedAt: quote.observedAt,
    })),
  })), [matches]);
  const constraint = useMemo(() => calculatorPassConstraint(calculatorMatches, selected), [calculatorMatches, selected]);
  const result = useMemo(() => calculateLotteryTicket(calculatorMatches, selected, { passType, multiplier }), [calculatorMatches, selected, passType, multiplier]);
  const maxPass = Math.min(8, Math.max(1, matches.length), Math.max(1, constraint.maxPassType));
  const setMultiplier = (value: number) => onMultiplierChange(Math.max(1, Math.min(50, Math.trunc(value || 1))));
  return <section className="calculator-bar" aria-label="竞彩计算器">
    <div className="calculator-bar-head"><div><span className="ai-kicker">官方竞彩计算器</span><h3>已选 {matches.length} 场 · {result.bets} 注</h3></div><button type="button" className="ghost" onClick={onClear}>清空</button></div>
    <div className="calculator-matches">{matches.map((match) => {
      const current = selections[match.scheduleId];
      const picks = current?.picks ?? [];
      return <div className="calculator-match" key={match.scheduleId}><div><b>{match.lottery?.number ?? match.scheduleId} {match.homeTeamName} vs {match.awayTeamName}</b>{picks.length > 0 ? picks.map((pick) => <span key={pick.market}>{MARKET_LABELS[pick.market]} · {pick.selections.map((value) => calculatorSelectionLabel(pick.market, value)).join(' / ')}</span>) : <span>请选择官方赛果</span>}</div><button type="button" className="ghost" onClick={() => onRemove(match.scheduleId)}>移除</button></div>;
    })}</div>
    <div className="pass-select"><span>过关方式</span><div>{Array.from({ length: maxPass }, (_, index) => index + 1).map((size) => <button key={size} type="button" className={passType === size ? 'active' : ''} onClick={() => onPassTypeChange(size)}>{size === 1 ? '单关' : size + '串1'}</button>)}</div></div>
    <div className="calculator-multiplier"><span>倍数</span><button type="button" className="ghost" onClick={() => setMultiplier(multiplier - 1)} disabled={multiplier <= 1}>−</button><input aria-label="投注倍数" inputMode="numeric" value={multiplier} onChange={(event) => setMultiplier(Number(event.target.value.replace(/\D/g, '')))} /><button type="button" className="ghost" onClick={() => setMultiplier(multiplier + 1)} disabled={multiplier >= 50}>+</button><small>1–50 倍</small></div>
    {constraint.reason && <div className="calculator-warning">{constraint.reason}</div>}
    {!result.executable && <div className="calculator-warning">{result.invalidReason ?? result.missing.slice(0, 4).join('；') ?? '请选择有效赛果'}</div>}
    {result.executable && <div className="calculator-summary"><div><span>总注数</span><b>{result.bets}</b></div><div><span>总本金</span><b>{result.stake.toFixed(2)} 元</b></div><div><span>最低奖金</span><b>{result.minPayout.toFixed(2)} 元</b></div><div><span>最高奖金</span><b>{result.maxPayout.toFixed(2)} 元</b></div>{result.settlementStatus === 'settled' && <div className="actual"><span>实际返还</span><b>{(result.actualPayout ?? 0).toFixed(2)} 元</b></div>}{result.settlementStatus === 'pending' && <div><span>实际返还</span><b>待结算</b></div>}</div>}
    {result.missing.length > 0 && <div className="calculator-warning">{result.missing.slice(0, 8).join('；')}</div>}
    {result.warnings.length > 0 && <div className="calculator-warning">{result.warnings.join('；')}</div>}
  </section>;
}